// Shared geometry + rendering for fortress patterns, used by the read-only
// catalogue (`FortressLibrary`) and the editor (`FortressEditor`).
//
// A pattern is a set of buildings at relative node-vector offsets. We anchor the
// offset frame on a real A-type node (`BASE`) so sublattice/parity line up for
// hex enumeration and path-finding.

import type { FortressPattern, FortressShape } from "@fortress/shared";
import {
  BRIDGE_FILL_COLOR,
  BRIDGE_OUTLINE_COLOR,
  I_BEAM,
  iBeamSegments,
} from "../bridgeIBeam";

export type Cube = { q: number; r: number; s: number };

const SQRT3 = Math.sqrt(3);
/** Node-vector → pixel (the board's pointy-top axial layout). */
export const px = (c: Cube) => ({ x: SQRT3 * (c.q + c.r / 2), y: 1.5 * c.r });
export const addC = (a: Cube, b: Cube): Cube => ({
  q: a.q + b.q,
  r: a.r + b.r,
  s: a.s + b.s,
});
export const subC = (a: Cube, b: Cube): Cube => ({
  q: a.q - b.q,
  r: a.r - b.r,
  s: a.s - b.s,
});
export const keyC = (c: Cube) => `${c.q},${c.r},${c.s}`;
export const parseC = (k: string): Cube => {
  const [q, r, s] = k.split(",").map(Number);
  return { q: q!, r: r!, s: s! };
};
/** Reflection across the q-axis (swaps r and s) — the board's mirror symmetry. */
export const mirrorC = (c: Cube): Cube => ({ q: c.q, r: c.s, s: c.r });

// A node sits at `3*hexCentre + cornerOffset`; clockwise order, so these also
// trace a hexagon outline.
export const CORNER_OFFSETS: Cube[] = [
  { q: 2, r: -1, s: -1 },
  { q: 1, r: 1, s: -2 },
  { q: -1, r: 2, s: -1 },
  { q: -2, r: 1, s: 1 },
  { q: -1, r: -1, s: 2 },
  { q: 1, r: -2, s: 1 },
];
export const BASE: Cube = { q: 2, r: -1, s: -1 };

export type Dir = "N" | "NE" | "SE" | "S" | "SW" | "NW";
export const EDGE: Record<Dir, Cube> = {
  NE: { q: 2, r: -1, s: -1 },
  NW: { q: -1, r: -1, s: 2 },
  S: { q: -1, r: 2, s: -1 },
  N: { q: 1, r: -2, s: 1 },
  SE: { q: 1, r: 1, s: -2 },
  SW: { q: -2, r: 1, s: 1 },
};
const A_DIRS: Dir[] = ["NE", "NW", "S"];
const B_DIRS: Dir[] = ["N", "SE", "SW"];
const subOf = (nv: Cube): "A" | "B" => (((nv.q % 3) + 3) % 3 === 2 ? "A" : "B");
const dirsFor = (nv: Cube): Dir[] => (subOf(nv) === "A" ? A_DIRS : B_DIRS);

function neighborsOf(nv: Cube): { dir: Dir; nv: Cube }[] {
  return dirsFor(nv).map((d) => ({ dir: d, nv: addC(nv, EDGE[d]) }));
}

/** The (up to 3) hex centres a node-vector is a corner of. */
export function incidentHexes(n: Cube): Cube[] {
  const out: Cube[] = [];
  for (const co of CORNER_OFFSETS) {
    const d = { q: n.q - co.q, r: n.r - co.r, s: n.s - co.s };
    if (d.q % 3 === 0 && d.r % 3 === 0 && d.s % 3 === 0) {
      out.push({ q: d.q / 3, r: d.r / 3, s: d.s / 3 });
    }
  }
  return out;
}

/** A honeycomb patch flooded `depth` edges out from BASE (the editor grid). */
export function buildGrid(depth: number): Cube[] {
  const seen = new Map<string, Cube>([[keyC(BASE), BASE]]);
  let frontier = [BASE];
  for (let i = 0; i < depth; i++) {
    const next: Cube[] = [];
    for (const nv of frontier) {
      for (const { nv: nb } of neighborsOf(nv)) {
        if (!seen.has(keyC(nb))) {
          seen.set(keyC(nb), nb);
          next.push(nb);
        }
      }
    }
    frontier = next;
  }
  return [...seen.values()];
}

/** Shortest honeycomb path (edge directions) from an A-type origin to target. */
export function pathFrom(origin: Cube, target: Cube): Dir[] {
  if (keyC(origin) === keyC(target)) return [];
  const seen = new Set([keyC(origin)]);
  let frontier: { nv: Cube; path: Dir[] }[] = [{ nv: origin, path: [] }];
  for (let depth = 0; depth < 12 && frontier.length; depth++) {
    const next: { nv: Cube; path: Dir[] }[] = [];
    for (const { nv, path } of frontier) {
      for (const { dir, nv: nb } of neighborsOf(nv)) {
        const k = keyC(nb);
        if (seen.has(k)) continue;
        const np = [...path, dir];
        if (k === keyC(target)) return np;
        seen.add(k);
        next.push({ nv: nb, path: np });
      }
    }
    frontier = next;
  }
  return [];
}

// --- Glyphs ---------------------------------------------------------------
//
// Building glyphs mirror the board's raised tokens (PixiGame's `drawBuilding`):
// the same outlines, chamfered facets flat-shaded from a top-left light, and a
// thin white rim — but in a neutral dark grey, since a card pattern belongs to
// no player. The wild (star) has no board piece, so it keeps its star outline
// and just gets the identical facet treatment.

export const SHAPE_LABEL: Record<string, string> = {
  scout: "Scout",
  barracks: "Barracks",
  mine: "Mine",
  wild: "Wild",
};

const TOKEN_GREY = 0x4f4f4f; // neutral dark grey — matches no player colour

/** The wild (star) glyph is drawn this much larger than its nominal radius —
 * `PatternDiagram`'s fit box accounts for the extra reach so it never clips. */
const WILD_STAR_SCALE = 1.5;

/** Lighten/darken a colour by a multiplicative factor (the board's `shade`). */
function shade(base: number, factor: number): string {
  const r = (base >> 16) & 0xff;
  const g = (base >> 8) & 0xff;
  const b = base & 0xff;
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(v * factor)));
  return `#${(((c(r) << 16) | (c(g) << 8) | c(b)) | 0x1000000).toString(16).slice(1)}`;
}

/** Outline polygon per kind, centred at (0,0) — the same proportions as the
 * board tokens' outlines (`buildingOutline`); `wild` adds the card-only star.
 * Scout returns `null` → drawn as a domed disc. */
function tokenOutline(kind: string, r: number): [number, number][] | null {
  switch (kind) {
    case "barracks": {
      const rr = r * 1.18; // a triangle looks small for its radius — bump it up
      return [
        [0, -rr],
        [rr * 0.866, rr * 0.5],
        [-rr * 0.866, rr * 0.5],
      ];
    }
    case "mine": {
      const s = r * 0.9; // half-side; a square reads heavy, so trim a touch
      return [
        [-s, -s],
        [s, -s],
        [s, s],
        [-s, s],
      ];
    }
    case "wild": {
      // A 5-point star's visual mass is tiny for its radius (thin points, small
      // inner ring), so like the triangle it gets a bump — a big one — to read
      // at the same weight as the discs/squares beside it.
      const R = r * WILD_STAR_SCALE;
      const pts: [number, number][] = [];
      for (let i = 0; i < 10; i++) {
        const rr = i % 2 ? R * 0.42 : R;
        const a = -Math.PI / 2 + (i * Math.PI) / 5;
        pts.push([rr * Math.cos(a), rr * Math.sin(a)]);
      }
      return pts;
    }
    default:
      return null; // scout → circle (disc)
  }
}

/** One building/marker glyph centred at (cx, cy). */
export function Glyph({
  kind,
  cx,
  cy,
  r = 7,
}: {
  kind: string;
  cx: number;
  cy: number;
  r?: number;
}) {
  switch (kind) {
    case "scout":
    case "mine":
    case "barracks":
    case "wild": {
      const rimW = Math.max(0.9, r * 0.17); // board rim: 1.2 at r ≈ 7, scaled
      const LX = -0.7071; // light direction (top-left)
      const LY = -0.7071;
      const ox = -r * 0.14; // top face / highlight nudged toward the light
      const oy = -r * 0.16;
      const outline = tokenOutline(kind, r);
      if (!outline) {
        // Scout: a domed disc — dark underside, mid body, lit cap to the light.
        return (
          <g>
            <circle cx={cx} cy={cy} r={r} fill={shade(TOKEN_GREY, 0.62)} />
            <circle
              cx={cx + ox * 0.8}
              cy={cy + oy * 0.8}
              r={r * 0.82}
              fill={shade(TOKEN_GREY, 1)}
            />
            <circle
              cx={cx + ox * 1.7}
              cy={cy + oy * 1.7}
              r={r * 0.45}
              fill={shade(TOKEN_GREY, 1.3)}
            />
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke="#fff"
              strokeWidth={rimW}
            />
          </g>
        );
      }
      // Chamfer into one flat facet per edge: the top face is the outline
      // scaled down and nudged toward the light; each facet fills the strip
      // from an outer edge to the matching top edge, shaded by how much that
      // edge faces the light, so the token looks beveled.
      const topScale = 0.58;
      const top = outline.map(
        ([x, y]) => [x * topScale + ox, y * topScale + oy] as [number, number],
      );
      const n = outline.length;
      const pt = ([x, y]: [number, number]) => `${cx + x},${cy + y}`;
      const facets = [];
      for (let i = 0; i < n; i++) {
        const a = outline[i]!;
        const b = outline[(i + 1) % n]!;
        const ta = top[i]!;
        const tb = top[(i + 1) % n]!;
        const mx = (a[0] + b[0]) / 2; // edge midpoint ≈ outward normal direction
        const my = (a[1] + b[1]) / 2;
        const len = Math.hypot(mx, my) || 1;
        const lit = (mx / len) * LX + (my / len) * LY; // -1 (away) .. 1 (toward)
        facets.push(
          <polygon
            key={i}
            points={`${pt(a)} ${pt(b)} ${pt(tb)} ${pt(ta)}`}
            fill={shade(TOKEN_GREY, 0.75 + 0.28 * lit)}
          />,
        );
      }
      return (
        <g>
          {facets}
          <polygon points={top.map(pt).join(" ")} fill={shade(TOKEN_GREY, 1.2)} />
          <polygon
            points={outline.map(pt).join(" ")}
            fill="none"
            stroke="#fff"
            strokeWidth={rimW}
          />
        </g>
      );
    }
    case "marker": {
      // The X scales with `r` (half-diagonal = r/2, so its full extent is `r`),
      // instead of a fixed pixel size, so it shrinks with the thumbnail like the
      // buildings do rather than dominating small diagrams.
      const d = r * 0.5;
      return (
        <path
          d={`M ${cx - d} ${cy - d} L ${cx + d} ${cy + d} M ${cx + d} ${cy - d} L ${cx - d} ${cy + d}`}
          stroke="#2b2620"
          strokeWidth={Math.max(1.2, r * 0.28)}
          strokeLinecap="round"
          fill="none"
        />
      );
    }
    default:
      return null;
  }
}

// --- Pattern ⇄ node-map helpers -------------------------------------------

/** Mirror every offset in a pattern (its reflected build orientation). */
export function mirrorPattern(p: FortressPattern): FortressPattern {
  return {
    buildings: p.buildings.map((b) => ({ shape: b.shape, offset: mirrorC(b.offset) })),
    markers: p.markers.map(mirrorC),
    bridges: p.bridges.map(([a, b]) => [mirrorC(a), mirrorC(b)]),
  };
}

/** Editor state (node-key → shape | "marker") → a FortressPattern. The editor
 * can't author bridges (they live on edges, not nodes), so a card's original
 * bridges are passed through untouched. */
export function mapToPattern(
  shapes: Map<string, string>,
  bridges: FortressPattern["bridges"] = [],
): FortressPattern {
  const buildings: { shape: FortressShape; offset: Cube }[] = [];
  const markers: Cube[] = [];
  for (const [k, v] of shapes) {
    const offset = subC(parseC(k), BASE);
    if (v === "marker") markers.push(offset);
    else buildings.push({ shape: v as FortressShape, offset });
  }
  return { buildings, markers, bridges };
}

// --- Static rendering -----------------------------------------------------

/** Draw a fortress pattern on its honeycomb: the hex cells the buildings sit on,
 * corner dots, then the building shapes and markers on their nodes. */
export function PatternDiagram({
  pattern,
  maxW = 210,
  maxH = 150,
  pad = 14,
  iconScale = 1.5,
}: {
  pattern: FortressPattern;
  maxW?: number;
  maxH?: number;
  /** Margin (px) around the pattern inside the SVG. Shrink it for small
   * thumbnails where the default would crowd out the pattern itself. */
  pad?: number;
  /** Overall glyph-size multiplier. The default (1.5) suits the large expanded
   * card face; small market/fortress tiles pass a smaller value so their icons
   * stay compact. */
  iconScale?: number;
}) {
  const patNodes = [
    ...pattern.buildings.map((b) => ({ nv: addC(BASE, b.offset), kind: b.shape as string })),
    ...pattern.markers.map((m) => ({ nv: addC(BASE, m), kind: "marker" })),
  ];
  const bridgeSegs = pattern.bridges.map(([a, b]) => ({
    a: addC(BASE, a),
    b: addC(BASE, b),
  }));
  if (patNodes.length === 0 && bridgeSegs.length === 0) {
    return <div style={{ fontSize: 11, color: "#999" }}>no pattern</div>;
  }
  const hexMap = new Map<string, Cube>();
  for (const pn of patNodes) for (const h of incidentHexes(pn.nv)) hexMap.set(keyC(h), h);
  for (const s of bridgeSegs) {
    for (const nv of [s.a, s.b]) {
      for (const h of incidentHexes(nv)) hexMap.set(keyC(h), h);
    }
  }
  const cornerDots = new Map<string, { x: number; y: number }>();
  const hexPolys = [...hexMap.values()].map((h) => {
    const c = { q: 3 * h.q, r: 3 * h.r, s: 3 * h.s };
    return CORNER_OFFSETS.map((co) => {
      const nv = addC(c, co);
      const p = px(nv);
      cornerDots.set(keyC(nv), p);
      return p;
    });
  });
  const glyphs = patNodes.map((pn) => ({ ...px(pn.nv), kind: pn.kind }));

  // Glyph radii, in px-units, as a factor of `scale` (buildings read as the
  // primary content; markers/X's smaller so they sit between the pieces). The
  // whole set is enlarged by `iconScale`.
  const buildingK = 0.82 * iconScale; // buildingR / scale
  const markerK = 0.56 * iconScale; // markerR / scale
  // How far a glyph reaches beyond its node centre, in px-units — the enlarged
  // wild star (1.5×R points) reaches farthest, then a mine's corner (≈1.2×R);
  // the X's arm and bridge flanges reach less.
  const buildingOver = buildingK * 1.25;
  const wildOver = buildingK * (WILD_STAR_SCALE + 0.1);
  const markerOver = markerK * 0.75;

  // Fit the content box (in px-units) INCLUDING glyph overhang, so the enlarged
  // glyphs never clip the SVG edge or spill past maxW/maxH.
  type Ext = { x: number; y: number; pad: number };
  const exts: Ext[] = [
    ...[...cornerDots.values()].map((p) => ({ x: p.x, y: p.y, pad: 0 })),
    ...patNodes.map((pn) => {
      const p = px(pn.nv);
      const over =
        pn.kind === "marker"
          ? markerOver
          : pn.kind === "wild"
            ? wildOver
            : buildingOver;
      return { x: p.x, y: p.y, pad: over };
    }),
    ...bridgeSegs.flatMap((s) =>
      iBeamSegments(px(s.a), px(s.b), iconScale)
        .flanges.flat()
        .map((f) => ({ x: f.x, y: f.y, pad: 0.35 })),
    ),
  ];
  const minX = Math.min(...exts.map((e) => e.x - e.pad));
  const maxX = Math.max(...exts.map((e) => e.x + e.pad));
  const minY = Math.min(...exts.map((e) => e.y - e.pad));
  const maxY = Math.max(...exts.map((e) => e.y + e.pad));
  const w = maxX - minX || 1;
  const h = maxY - minY || 1;
  const scale = Math.min(15, (maxW - 2 * pad) / w, (maxH - 2 * pad) / h);
  const sx = (x: number) => pad + (x - minX) * scale;
  const sy = (y: number) => pad + (y - minY) * scale;
  const cw = w * scale + 2 * pad;
  const ch = h * scale + 2 * pad;
  const buildingR = Math.max(3 * iconScale, scale * buildingK);
  const markerR = Math.max(2.5 * iconScale, scale * markerK);
  const glyphR = buildingR;

  return (
    <svg width={cw} height={ch} viewBox={`0 0 ${cw} ${ch}`} style={{ display: "block" }}>
      {hexPolys.map((pts, i) => (
        <polygon
          key={`h${i}`}
          points={pts.map((p) => `${sx(p.x)},${sy(p.y)}`).join(" ")}
          fill="none"
          stroke="#cdc4ab"
          strokeWidth={1.2}
          strokeLinejoin="round"
        />
      ))}
      {[...cornerDots.values()].map((p, i) => (
        <circle key={`d${i}`} cx={sx(p.x)} cy={sy(p.y)} r={1.5} fill="#9a917b" />
      ))}
      {/* Bridges: the same I-beam glyph the board draws (`iBeamSegments`) — a
          web along the edge with a flange at each end, stroked as a dark outline
          then a grey fill. Buildings/markers draw on top, as on the card art. */}
      {bridgeSegs.map((s, i) => {
        const pa = px(s.a);
        const pb = px(s.b);
        const seg = iBeamSegments(
          { x: sx(pa.x), y: sy(pa.y) },
          { x: sx(pb.x), y: sy(pb.y) },
          iconScale,
        );
        const d =
          `M ${seg.web[0].x} ${seg.web[0].y} L ${seg.web[1].x} ${seg.web[1].y} ` +
          seg.flanges
            .map(([p, q]) => `M ${p.x} ${p.y} L ${q.x} ${q.y}`)
            .join(" ");
        return (
          <g key={`b${i}`} strokeLinecap="round" fill="none">
            <path
              d={d}
              stroke={BRIDGE_OUTLINE_COLOR}
              strokeWidth={seg.len * I_BEAM.outlineWidth * iconScale}
            />
            <path
              d={d}
              stroke={BRIDGE_FILL_COLOR}
              strokeWidth={seg.len * I_BEAM.fillWidth * iconScale}
            />
          </g>
        );
      })}
      {glyphs.map((n, i) => (
        <Glyph
          key={`g${i}`}
          kind={n.kind}
          cx={sx(n.x)}
          cy={sy(n.y)}
          r={n.kind === "marker" ? markerR : buildingR}
        />
      ))}
    </svg>
  );
}

/** A pattern and its mirror, side by side with a divider — matches the cards,
 * where both build orientations are shown (the matcher tries reflections). */
export function MirrorPair({ pattern }: { pattern: FortressPattern }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      <PatternDiagram pattern={pattern} maxW={150} maxH={130} />
      <div style={{ width: 1, alignSelf: "stretch", background: "#d8cfb8" }} />
      <PatternDiagram pattern={mirrorPattern(pattern)} maxW={150} maxH={130} />
    </div>
  );
}

// --- Editable grid --------------------------------------------------------

/** Cycle order when a node is clicked. */
export const CYCLE: (string | undefined)[] = [
  undefined,
  "scout",
  "barracks",
  "mine",
  "wild",
  "marker",
];

/** An interactive honeycomb: click a node to cycle its shape. `shapes` maps a
 * node key → shape|"marker"; `onCycle` is called with the clicked node key. */
export function EditableGrid({
  grid,
  shapes,
  onCycle,
}: {
  grid: Cube[];
  shapes: Map<string, string>;
  onCycle: (key: string) => void;
}) {
  const hexMap = new Map<string, Cube>();
  for (const nv of grid) for (const h of incidentHexes(nv)) hexMap.set(keyC(h), h);
  const cornerDots = new Map<string, { x: number; y: number }>();
  const hexPolys = [...hexMap.values()].map((h) => {
    const c = { q: 3 * h.q, r: 3 * h.r, s: 3 * h.s };
    return CORNER_OFFSETS.map((co) => {
      const nv = addC(c, co);
      const p = px(nv);
      cornerDots.set(keyC(nv), p);
      return p;
    });
  });

  const allP = [...cornerDots.values()];
  const minX = Math.min(...allP.map((p) => p.x));
  const maxX = Math.max(...allP.map((p) => p.x));
  const minY = Math.min(...allP.map((p) => p.y));
  const maxY = Math.max(...allP.map((p) => p.y));
  const pad = 16;
  const scale = 16;
  const sx = (x: number) => pad + (x - minX) * scale;
  const sy = (y: number) => pad + (y - minY) * scale;
  const cw = (maxX - minX) * scale + 2 * pad;
  const ch = (maxY - minY) * scale + 2 * pad;

  return (
    <svg
      width={cw}
      height={ch}
      viewBox={`0 0 ${cw} ${ch}`}
      style={{ display: "block", background: "#fbf7ec", borderRadius: 8 }}
    >
      {hexPolys.map((pts, i) => (
        <polygon
          key={`h${i}`}
          points={pts.map((p) => `${sx(p.x)},${sy(p.y)}`).join(" ")}
          fill="none"
          stroke="#d8cfb8"
          strokeWidth={1.2}
          strokeLinejoin="round"
        />
      ))}
      {grid.map((nv, i) => {
        const k = keyC(nv);
        const p = px(nv);
        const v = shapes.get(k);
        return (
          <g key={`n${i}`} style={{ cursor: "pointer" }} onClick={() => onCycle(k)}>
            {v ? (
              <Glyph kind={v} cx={sx(p.x)} cy={sy(p.y)} r={8} />
            ) : (
              <circle cx={sx(p.x)} cy={sy(p.y)} r={2.6} fill="#b3aa92" />
            )}
            <circle cx={sx(p.x)} cy={sy(p.y)} r={10} fill="transparent" />
          </g>
        );
      })}
    </svg>
  );
}

/** Serialise editor state to a `pat(...)` call to paste into config/fortress.ts.
 * Anchors at an A-type building (top-left) so the first building reads `b(...)`.
 * The editor can't author bridges, so a card's original `bridges` are passed in
 * and re-emitted as `br(...)` paths (they'd otherwise be silently dropped). */
export function patternToTs(
  shapes: Map<string, string>,
  bridges: FortressPattern["bridges"] = [],
): string {
  const entries = [...shapes.entries()].map(([k, v]) => ({ nv: parseC(k), v }));
  if (entries.length === 0 && bridges.length === 0) return "pat([])";
  const buildings = entries.filter((e) => e.v !== "marker");
  const markers = entries.filter((e) => e.v === "marker");

  const topLeft = <T extends { nv: Cube }>(list: T[]): T[] =>
    [...list].sort((a, b) => {
      const pa = px(a.nv);
      const pb = px(b.nv);
      return pa.y - pb.y || pa.x - pb.x;
    });

  const aBuildings = buildings.filter((e) => subOf(e.nv) === "A");
  const origin = aBuildings.length ? topLeft(aBuildings)[0]!.nv : BASE;

  const dirArgs = (nv: Cube) =>
    pathFrom(origin, nv)
      .map((d) => `"${d}"`)
      .join(", ");

  const bLines = topLeft(buildings).map((e) => {
    const args = dirArgs(e.nv);
    return `  b("${e.v}"${args ? `, ${args}` : ""})`;
  });
  const mLines = topLeft(markers).map((e) => `[${dirArgs(e.nv)}]`);

  // A bridge serialises as the path to its nearer endpoint plus the step that
  // crosses the bridged edge (br()'s last step).
  const brLines = bridges.map(([a, b]) => {
    let nvA = addC(BASE, a);
    let nvB = addC(BASE, b);
    if (pathFrom(origin, nvB).length < pathFrom(origin, nvA).length) {
      [nvA, nvB] = [nvB, nvA];
    }
    const cross = dirsFor(nvA).find((d) => keyC(addC(nvA, EDGE[d])) === keyC(nvB));
    if (!cross) return "br(/* endpoints not adjacent */)";
    const steps = [...pathFrom(origin, nvA).map((d) => `"${d}"`), `"${cross}"`];
    return `br(${steps.join(", ")})`;
  });

  const buildingsBlock = `[\n${bLines.join(",\n")}\n]`;
  const args = [buildingsBlock];
  if (markers.length || brLines.length) args.push(`[${mLines.join(", ")}]`);
  if (brLines.length) args.push(`[${brLines.join(", ")}]`);
  return `pat(${args.join(", ")})`;
}
